/**
 * Map Entra group names to Tier-1 role identifiers. Mirrors the table in
 * docs/architecture.md §2.4. The same mapping lives in the API
 * (apps/api-functions-core/src/shared/auth/rbac.ts) — keep them in sync.
 *
 * A user may be in multiple groups and therefore hold multiple roles.
 * `primaryRole()` picks the highest-privilege role for dashboard selection.
 */

export type Tier1Role =
  | 'c_level'
  | 'it_admin'
  | 'central_ops'
  | 'hr_ops'
  | 'district_manager'
  | 'regional_manager'
  | 'store_manager'
  | 'vendor';

const ROLE_PRIORITY: Tier1Role[] = [
  'c_level',
  'it_admin',
  'central_ops',
  'hr_ops',
  'district_manager',
  'regional_manager',
  'store_manager',
  'vendor',
];

export const ENTRA_GROUP_PREFIX = 'TMKN-Qargo-';

/**
 * Entra emits security-group *object IDs* (not display names) in the `groups`
 * claim for cloud-only groups, so the name-based mapping below can't match.
 * This map translates the known group OIDs back to names. Sourced from
 * VITE_ENTRA_GROUP_OID_MAP (JSON: { "<oid>": "TMKN-Qargo-Admins", ... }) so
 * it's environment-specific and not hardcoded per tenant.
 */
const OID_TO_NAME: Record<string, string> = (() => {
  try {
    return JSON.parse(import.meta.env.VITE_ENTRA_GROUP_OID_MAP ?? '{}') as Record<string, string>;
  } catch {
    return {};
  }
})();

function groupName(g: string): string {
  return OID_TO_NAME[g] ?? g;
}

export function rolesFromGroups(rawGroups: readonly string[]): Tier1Role[] {
  const groups = rawGroups.map(groupName);
  const roles = new Set<Tier1Role>();
  for (const g of groups) {
    if (g === 'TMKN-Qargo-CLevel') roles.add('c_level');
    else if (g === 'TMKN-Qargo-Admins') roles.add('it_admin');
    else if (g === 'TMKN-Qargo-CentralOps') roles.add('central_ops');
    else if (g === 'TMKN-Qargo-HR') roles.add('hr_ops');
    else if (g.startsWith('TMKN-Qargo-District-')) roles.add('district_manager');
    else if (g.startsWith('TMKN-Qargo-Regional-')) roles.add('regional_manager');
    else if (g.startsWith('TMKN-Qargo-Mgr-')) roles.add('store_manager');
    else if (g.startsWith('TMKN-Qargo-Vendor-')) roles.add('vendor');
  }
  return ROLE_PRIORITY.filter((r) => roles.has(r));
}

export function primaryRole(groups: readonly string[]): Tier1Role | null {
  return rolesFromGroups(groups)[0] ?? null;
}

export function hasAnyGroup(userGroups: readonly string[], required: readonly string[]): boolean {
  if (required.length === 0) return true;
  // Token groups may be raw Entra OIDs; translate to names (like the
  // predicates below and the API middleware do) before matching.
  const set = new Set(userGroups.map(groupName));
  return required.some((g) => set.has(g));
}

/**
 * True if the user's groups grant access to the Tier-2 user admin UI:
 * Admins / CLevel / HR (org-wide), CentralOps (read), or any
 * Mgr-{StoreCode} / Regional-* / District-* (store-scoped).
 *
 * Mirrors the backend gate in
 * apps/api-functions-identity/src/shared/auth/adminAuthz.ts.
 */
export function canManageBaristaUsers(rawGroups: readonly string[]): boolean {
  return rawGroups
    .map(groupName)
    .some(
      (g) =>
        g === 'TMKN-Qargo-Admins' ||
        g === 'TMKN-Qargo-CLevel' ||
        g === 'TMKN-Qargo-HR' ||
        g === 'TMKN-Qargo-CentralOps' ||
        g.startsWith('TMKN-Qargo-Mgr-') ||
        g.startsWith('TMKN-Qargo-Regional-') ||
        g.startsWith('TMKN-Qargo-District-'),
    );
}

/**
 * True if the user is HR staff: HR / Admins / CLevel. Gates the HR profile page
 * (hiring notes, pay PII, warnings) and the ticket "resolve" action. Mirrors the
 * server gate in apps/api-functions-core/src/functions/hrProfiles.ts (HR_GROUPS)
 * and the `isHrStaff` check in hrTickets.ts.
 */
export function isHrStaff(rawGroups: readonly string[]): boolean {
  return rawGroups
    .map(groupName)
    .some((g) => g === 'TMKN-Qargo-HR' || g === 'TMKN-Qargo-Admins' || g === 'TMKN-Qargo-CLevel');
}
